/**
 * English translation bundle. Mirrors `vi.ts` structure exactly — every
 * key must exist with the same shape.
 */

const en = {
  common: {
    // GA-M-05/GA-W-07 hardcode sweep (2026-06-05) — moved out of TSX
    todayShort: "Today",
    processing: "Processing",
    daysShort: "{{d}}d",
    weeksShort: "{{n}}w",
    monthsShort: "{{n}}mo",
    backToDashboard: "← Back to dashboard",
    a11yClose: "Close",
    a11yCloseDialog: "Close dialog",
    a11yOpenGuide: "Open the 30-second guide",
    a11yNewUpdates: "New updates",
    a11yNewItems: "{{count}} new items",
    appName: "iSale",
    save: "Save",
    cancel: "Cancel",
    close: "Close",
    clear: "Clear",
    delete: "Delete",
    confirm: "Confirm",
    continue: "Continue",
    back: "Back",
    backTo: "Back to {{parent}}",
    next: "Next →",
    loading: "Loading…",
    search: "Search…",
    saving: "Saving…",
    creating: "Creating…",
    error: "Something went wrong",
    retry: "Retry",
    skip: "Skip",
    done: "Done",
    yes: "Yes",
    no: "No",
    seeAll: "See all →",
    saveFailed: "Couldn't save · {{error}}",
    deleteFailed: "Couldn't delete · {{error}}",
    createFailed: "Couldn't create {{what}} · {{error}}",
    loadFailed: "Couldn't load · {{error}}",
    createClipFailed: "Couldn't create clip · {{error}}",
    createCheckoutFailed: "Couldn't start checkout · {{error}}",
    deleteAccountFailed: "Couldn't delete account · {{error}}",
    // EP-8 offline banner
    offlineBanner: "No internet connection — showing saved data",
    offlineBannerNoCache: "No internet connection — reconnect to load data",
  },
  splash: {
    /** Tagline below the iSale wordmark on the boot splash (Hi-Fi 01). */
    tagline: "AI selling clips · in 3 minutes",
  },
  signIn: {
    heroLine: "Open the app,\n3-min clip ready.",
    heroLine1: "Open the app,",
    heroLine2: "3-min clip ready.",
    subtitle:
      "Sign in to make your first clip — ready for TikTok, Reels, Shorts & Shopee Video, no editing skills needed.",
    subCopy:
      "Sign in to make your first clip — ready for TikTok, Reels, Shorts & Shopee Video, no editing skills needed. Syncs with the iSale app on iPhone & Android.",
    tagline: "AI sales clips · web",
    googleButton: "Continue with Google",
    appleButton: "Continue with Apple",
    continueGoogle: "Continue with Google",
    continueApple: "Continue with Apple",
    continueEmail: "Sign in with email",
    or: "or",
    redirecting: "Redirecting…",
    appleNotConfigured: "Apple Sign-In is coming. Please use Google for now.",
    emailComingSoon: "Email sign-in lands in Phase 2. Use Google or Apple for now.",
    /** FL-6: shown when APPLE_CLIENT_ID is not configured (Apple button hidden). */
    emailComingSoonGoogleOnly: "Email sign-in lands in Phase 2. Please use Google for now.",
    trustSpeed: "~3 min / clip",
    trustNative: "Made for Vietnam",
    legalPrefix: "By signing in you agree to our",
    legalTerms: "Terms",
    legalPrivacy: "Privacy Policy",
    legalAnd: "and",
    terms: "Terms",
    privacy: "Privacy Policy",
    captionHook: "Dry skin like the desert?",
  },
  home: {
    greetingMorning: "Good morning",
    greetingAfternoon: "Good afternoon",
    greetingEvening: "Good evening",
    greetingNight: "Burning the midnight oil",
    quotaLabel: "THIS MONTH",
    quotaUsageSuffix: "videos used",
    upgradeCta: "Upgrade →",
    recentProducts: "Recent products",
    recentClips: "Recent clips",
    fabLabel: "New clip",
    statusReady: "Awaiting render",
    statusDraft: "Draft",
    statusArchived: "Archived",
    statusRendered: "Completed",
    greetingAvatarA11y: "Open Account",
    greetingBellA11y: "Open Notifications",
    emptyClipsHint: "No clips yet — tap \"{{cta}}\" to start",
  },
  settings: {
    // GA-X-01 Option B (2026-06-05): toggle → real status row
    aiConsentAccepted: "Accepted · {{date}}",
    aiConsentNeedsReconsent: "Please re-consent to updated terms",
    aiConsentNotAccepted: "Not accepted yet",
    title: "Settings",
    sectionPermissions: "Permissions & data",
    sectionNotifications: "Notifications",
    sectionOther: "Other",
    sectionAccount: "Account",
    signOutNote: "End your session on this device",
    aiConsent: "AI consent",
    aiConsentNote: "3 providers · since May 2026",
    auditLog: "AI activity log",
    auditLogNote: "See every AI call",
    requestDelete: "Request data deletion",
    requestDeleteNote: "GDPR · ATTP",
    notifyRender: "When clip render finishes",
    notifyTips: "Video selling tips",
    notifyPromo: "Plan promos",
    language: "Language",
    appearance: "Appearance",
    themeLight: "Light",
    themeDark: "Dark",
    themeSystem: "System",
    signOut: "Sign out",
    accountGuest: "Guest",
    accountUserFallback: "Hi there 👋",
    accountWorkspaceFallback: "Personal workspace",
    accountPlanFree: "Free plan",
    requestDeleteNoteLong: "GDPR · ATTP · 30-day soft-delete",
    complianceDict: "Content compliance rules",
    complianceDictNote: "See every keyword the AI flags · updated weekly",
    aiConsentNoteShort: "3 providers · since May 2026",
    auditLogNoteShort: "See every AI call",
    billingMenuTitle: "Plan & quota",
    billingMenuNote: "View quota · upgrade to Pro",
    notificationsMenuTitle: "Notifications",
    notificationsMenuNote: "Render, quota, promo",
    footerBuild: "iSale v0.1.0 · build {{date}}",
  },
  billing: {
    title: "Pricing",
    currentPlan: "CURRENT PLAN",
    videosThisMonth: "videos this month",
    resetOn: "Resets",
    starterName: "Starter",
    starterTagline: "New shops · 30 clips = enough to test the market",
    proName: "Pro",
    proTagline: "Scaling shops · 5 clips/day to stay on trend",
    enterprise: "Enterprise",
    contactSales: "Contact",
    chooseStarter: "Choose Starter",
    choosePro: "Choose Pro",
    using: "Current",
    freeName: "Free",
    freeTagline: "Enough to try + post a few clips",
    lifetimeName: "Lifetime",
    lifetimeTagline: "Pay once, use forever — first 200 buyers only",
    registerEarly: "Register early",
    popular: "MOST POPULAR",
    footer:
      "Cancel anytime · 7-day refund · Pay via Momo, ZaloPay, Visa",
    // MP11 §3.3 — Pro Annual upsell
    proAnnualTitle: "Pro · Annual",
    proAnnualPrice: "{{price}}/yr",
    /** Shown inline in the Annual toggle pill — quick visual nudge that
     *  Annual carries a saving (P3.3, 2026-05-25). The actual saving
     *  amount is computed from price diffs in Billing.tsx and rendered
     *  via the savings pill below the toggle. */
    periodSavingsBadge: "save 2 mo",
    /** Prominent savings call-out under the toggle when Annual is picked.
     *  Replaces the bland "Equivalent to $X/mo" subtitle with a clear
     *  monetary win. {{saved}} = total $ saved per year vs paying monthly. */
    proAnnualSavings: "Save {{saved}}/yr vs monthly",
    proAnnualSaveBadge: "Save 20%",
    proAnnualSubtitle: "Equivalent to {{monthly}}/mo",
    periodSelectMonth: "Monthly",
    periodSelectYear: "Annual",
  },
  facts: {
    title: "Fact Locker",
    headerHook: "Verify first, generate second",
    heroTitle: "Confirm facts the AI found",
    introBody:
      "Each fact below must be verified before iSale sends it to the script LLM. This is the anti-hallucination guardrail — you are the final authority for every claim.",
    verifiedLabel: "Verified",
    verifiedNeeded: "required",
    riskyWarningTitle: "facts flagged by compliance",
    riskyWarningBody:
      "Facts marked ⚠️ contain wording that breaks VN rules (cosmetic, supplement, pharma…). Edit or confirm with evidence before verifying.",
    fieldSellingPoint: "Key selling point",
    fieldTargetAudience: "Target audience",
    fieldIngredient: "Ingredients",
    fieldClaim: "Claim",
    fieldCategoryInference: "AI category guess",
    ctaCreateScript: "Create clip →",
    ctaCreateScriptNeeded: "Verify {{n}} more fact(s)",
    createScriptFailed: "Couldn't create the script · try again",
    productNotReady: "Product isn't ready yet — wait for AI analysis to finish, then try again",
    addFieldCta: "Add",
    emptyFieldHint: "— no facts yet —",
  },
  clipFlow: {
    entryCta: "Create clip",
    resumeTitle: "Resume your clip",
    resumeCta: "Resume →",
    resumeChip: "↻ In progress",
    step1: "Product",
    step2: "Script",
    step3: "Edit",
    step4: "Export",
    stepLabel: "Step {{n}}/4",
    navNext: "Next →",
    navBack: "← Back",
    navSaveExit: "Save draft & exit",
    exitConfirmTitle: "Leave clip creation?",
    exitConfirmBody: "Go back to Home? You can resume this draft later.",
    exitConfirmLeave: "Leave",
    factGateTitle: "Confirm so AI writes it right",
    factGateBody: "The AI only writes from facts you've confirmed — nothing made up.",
    factGateProgress: "{{done}}/{{total}} confirmed",
    factGateDone: "All confirmed ✓",
    factGateRegulated: "⚠ docs required",
    captionLabel: "Post caption",
    captionRegenerate: "↻ Regenerate caption",
    exportReady: "Your clip is ready 🎉",
    exportPlatforms: "Post to platform",
    finishCta: "Done · Make another clip →",
    entryNoProduct: "Pick a product to start creating a clip.",
    entryProductError:
      "Couldn't open this product — it may be deleted or you don't have access.",
    entryMissing: "Missing data for this step. Please start again from a product.",
    entryPickProduct: "Pick a product →",
    advancedTitle: "Advanced options",
    advancedHint: "Pick a script format — defaults to Review",
    advancedOpen: "Open ▾",
    advancedClose: "Collapse ▴",
  },
  clipFormats: {
    review: "Review",
    unboxing: "Unboxing",
    beforeAfter: "Before–After",
    demo: "How-to / demo",
    story: "Story",
    promotion: "Promotion",
    testimonial: "Testimonial",
    faq: "FAQ",
  },
  clipTemplates: {
    // GA-M-05/GA-W-07 hardcode sweep (2026-06-05)
    unknownType: "This clip type doesn't exist",
    detailNextStepHint: "Next: enter the product name + upload photos",
    sectionTitle: "Create clip from template",
    sectionSubtitle: "iSale writes scripts with the right structure + tone for your audience",
    sectionBadge: "★ New",
    libraryTitle: "Pick the clip type you want",
    libraryIntro: "Each type has its own script structure — pick one of the 4 below.",
    detailBreadcrumbLibrary: "Clip templates",
    ctaSeeAll: "See all",
    ctaUseTemplate: "Use this template →",
    ctaUseTemplateRegulated: "I understand — Use this template",
    ctaCardSeeDetail: "See details →",
    unboxing: {
      name: "Unboxing",
      shortDesc: "Show the real product · build trust",
      useWhen: "New arrivals, beautiful packaging, build credibility",
      step1: "Curiosity-grabbing unboxing hook",
      step2: "Product reveal & details",
      step3: "Highlights when holding it",
      step4: "First impression",
      step5: "Soft CTA",
    },
    flashSale: {
      name: "Flash sale",
      shortDesc: "Push fast purchase · urgent offer",
      useWhen: "Sale events, clearance, limited-time deals",
      step1: "Shock-price hook",
      step2: "Product + specific offer",
      step3: "Reasons to buy now (urgency)",
      step4: "Strong CTA — Order now",
    },
    review: {
      name: "Review",
      shortDesc: "Real review · experience",
      useWhen: "Convince via experience, multiple selling points",
      step1: "Hook stating customer pain",
      step2: "Product as solution",
      step3: "2-3 main strengths",
      step4: "Real experience",
      step5: "CTA",
    },
    beforeAfter: {
      name: "Before–After",
      shortDesc: "Compare results · cosmetics",
      useWhen: "Visible-result products (cosmetics, supplements)",
      step1: "\"Before\" condition",
      step2: "Product introduction",
      step3: "Usage process",
      step4: "\"After\" result",
      step5: "CTA",
    },
    audience: {
      pickerLabel: "Audience",
      pickerHint: "(pick 1)",
      general: { name: "General", tone: "Neutral, approachable" },
      genZ: { name: "Gen Z (16–24)", tone: "Trendy, casual language" },
      office: { name: "Office (25–35)", tone: "Busy — emphasize convenience + quality" },
      mom: { name: "Moms", tone: "Safety-focused, for child / family" },
      male: { name: "Male", tone: "Direct, less flowery" },
      mature: { name: "Mature (40+)", tone: "Clear, trustworthy, no slang" },
    },
    complianceChip: "⚠ QC required",
    complianceCardTitle: "Cosmetic / supplement ad notice",
    complianceCardBody: "This clip type may run afoul of Decree 181/2013/NĐ-CP. iSale will check the script before rendering — if risky efficacy claims appear, we'll suggest edits or require certification.",
    complianceSeeRules: "View full regulations",
    metaDuration: "Suggested duration",
    metaRender: "Render template",
    metaSubtitle: "Subtitle",
    scriptStructureTitle: "Script structure",
    descriptionTitle: "What this template does",
    emptyTitle: "Couldn't load templates",
    emptyBody: "Connection seems slow. We'll retry in a moment — or tap below to try now.",
    emptyRetry: "Retry",
    emptyFallback: "Create clip without a template",
    contextChip: "Template: {{type}} · {{audience}}",
    contextChipRemove: "Remove template",
  },
  factsMobile: {
    // GA-M-05 a11y labels (2026-06-05)
    a11yVerify: "Verify",
    a11yUnverify: "Unverify",
    a11yEdit: "Edit",
    a11yDelete: "Delete",
    pullToRefresh: "Pull to refresh",
    loadingLabel: "Loading facts...",
    sourceVision: "AI detected",
    sourceUserInput: "You added",
    sourceVerified: "Verified",
    regulatedChip: "⚠ Sensitive",
    riskyAlertTitle: "{{n}} risky claim(s)",
    riskyAlertDetails: "View details →",
    riskySheetTitle: "Claims requiring certification",
    riskySheetSubtitle: "Vietnamese advertising regulation · cosmetics / supplements",
    riskySheetBody: "iSale detected {{n}} claim(s) in this product flagged as regulated. If you have certification, mark below — iSale will lower the warning in the script.",
    riskySheetAckLabel: "I have certification",
    riskySheetCtaSave: "Save acknowledgment",
    riskySheetCtaLater: "Later",
    riskySheetInfoTitle: "📚 Learn more",
    riskySheetInfoLine1: "Regulated claims still render — iSale adds disclaimer in caption",
    riskySheetInfoLine2: "If no certification, consider removing to avoid legal risk",
    saveShortcutHint: "(⌘S)",
    editSheetTitle: "Edit fact",
    createSheetTitle: "Add fact",
    sheetValueLabel: "Value",
    sheetCharCount: "{{n}} / 200",
    sheetCancel: "Cancel",
    sheetSave: "Save",
    sheetDelete: "🗑 Delete this fact",
    sheetDeleteConfirmTitle: "Delete this fact?",
    sheetSourceInfo: "Editing this sets the source to 'entered by you' — verify again to use it in a script",
    addedToast: "Fact added",
    deletedToast: "Fact deleted",
    saveFailedToast: "Couldn't save · {{err}}",
    deleteFailedToast: "Couldn't delete · {{err}}",
    emptyStateTitle: "No facts yet",
    emptyStateBody:
      'Go back to product detail and tap "Analyze with AI" so iSale can read the photos and suggest facts. Or add a fact manually below.',
    inputPlaceholder: "e.g. SPF50+, non-greasy, ≤ 60 chars",
    riskyAccessibilityHint:
      "Open sheet listing regulated claims with option to mark certification",
    /** FL-5 / GA-M-05: a11y label for the risky-claims banner (replaces hardcoded VN). */
    riskyAccessibilityLabel:
      "{{n}} risky claims, double-tap to see details and confirm you have documentation",
    saveChangesCta: "Save changes",
    addCta: "Add fact",
    editSourceHint:
      "Editing this sets the source to 'entered by you' — verify again to use it when generating a script.",
    createSourceHint: "This is saved with the source 'entered by you'. Verify it if you're sure.",
    fieldLabel: "field: {{field}}",
    addFailed: "Couldn't add · {{error}}",
  },
  factsWeb: {
    // GA-W-07 hardcode sweep (2026-06-05)
    emptyGroup: "— no facts yet —",
    title: "Verify facts",
    breadcrumb: "Verify facts",
    introBody: "Verify every fact below before iSale writes the script. This is the anti-hallucination guard.",
    progressDone: "{{n}}/{{total}} verified",
    verifiedPercent: "{{p}}% · need {{n}} more verified to unlock",
    riskyWarningCount: "{{n}} risky claim(s)",
    riskyWarningDetails: "View details",
    ctaCreateScript: "Create clip →",
    ctaCreateScriptNeeded: "Need {{n}} more verified",
    emptyTitle: "Product not analyzed yet",
    emptyBody: "iSale hasn't read this product's photos yet — go back to detail and tap \"Analyze AI\" so we can extract facts. Takes ~25s.",
    emptyCtaPrimary: "🧠 Analyze with AI",
    emptyCtaBack: "← Back",
    drawerEditTitle: "Edit fact",
    drawerCreateTitle: "Add fact",
    drawerSave: "Save",
    drawerSaveShortcut: "(⌘S)",
    drawerCancel: "Cancel",
    drawerValueLabel: "Value",
    drawerValueMaxHint: "Max 200 characters",
    drawerCharCount: "{{n}} / 200",
    drawerDelete: "Delete this fact",
    deleteConfirmTitle: "Delete this fact?",
    deleteConfirmAction: "Delete",
    deleteConfirmCancel: "Cancel",
    addFactCta: "+ Add {{field}}",
    riskyModalTitle: "Claims requiring certification",
    riskyModalSubtitle: "Vietnamese advertising regulation · cosmetics / supplements",
    riskyModalBody: "iSale detected claims in this product flagged as regulated under Decree 181/2013/NĐ-CP. If you have certification, mark below — iSale will lower the warning in the script.",
    riskyModalAckLabel: "I have certification",
    riskyModalGroupLabel: "Group: {{group}}",
    riskyModalCtaSave: "Save acknowledgment",
    riskyModalCtaLater: "Later",
    riskyModalInfoTitle: "📚 Learn more",
    sourceLabel: "{{source}} · confidence {{c}}%",
    factVerifiedLabel: "Verified · {{c}}%",
    factPendingLabel: "Pending · {{c}}%",
    factRiskyLabel: "⚠ Regulated · certification required",
    helpTopic: "facts-web",
    loadingLabel: "Loading facts...",
    loadError: "Couldn't load facts — check connection",
    retryAction: "Retry",
    saveFailedToast: "Couldn't save",
    deleteFailedToast: "Couldn't delete",
    createScriptFailed: "Couldn't create the script · try again",
    productNotReady: "Product isn't ready yet — wait for AI analysis to finish, then try again",
  },
  render: {
    title: "Render",
    chooseMode: "Pick render mode",
    chooseModeTitle: "Preview or final?",
    fastPreview: "Fast preview",
    fastPreviewNote: "~60-90s · iSale watermark · 720p · no quota cost",
    finalRender: "Final render",
    finalRenderNote: "~3 min · 1080×1920 · plan-tier watermark · 1 video used",
    pipelineLabel: "AI processing steps",
    pipelineHint: "Do other stuff — we'll notify when ready.",
    stageScript: "Generate script & storyboard",
    stageTts: "Voice-over",
    stageCompose: "Compose video",
    stageExport: "Export MP4 9:16",
    stageRunning: "running",
    longLoadingTitle: "Tips for next time",
    longLoadingTip1: "Use fewer than 5 input images",
    longLoadingTip2: "Disable AI hero scene if not needed",
    longLoadingTip3: "Render during off-peak (after 11 PM)",
    errorTitle: "Something went wrong",
    errorEmpathy: "Not your fault — we'll retry for free, no quota charged.",
    errorRecommend: "RECOMMENDED",
    errorBackHome: "Later · Back home",
    // REM-016 parity fix — render.tsx keys missing from the mobile `render`
    // namespace (were rendering as literal key-paths). Mirrored from renderWeb.
    initJob: "Preparing…",
    pollingHint:
      "iSale is processing — feel free to close the app, we'll push when done.",
    etaRemaining: "~{{seconds}}s left",
    tipCloseApp: "💡 Tip: You can close the app — we'll notify you when it's done.",
    stepLabel: "Step: {{step}}",
    stepFallback: "initialising",
    doneTitle: "Render done",
    doneBody: "Tap Next to export MP4 + thumbnail.",
    cancelledTitle: "Cancelled",
    detailsTitle: "Technical details",
    detailsToggleShow: "Show technical details",
    detailsToggleHide: "Hide details",
    rowId: "Render ID",
    rowType: "Type",
    rowStatus: "Status",
    rowProgress: "Progress",
    rowStep: "Step",
    rowWatermark: "Watermark",
    rowCdnUrl: "CDN URL",
    watermarkYes: "Yes",
    watermarkNo: "No",
    fallbackDash: "—",
    cancelCta: "Cancel render",
    successToast: "Render done · your clip is ready 🎉",
    previewQueueFailed: "Couldn't queue preview · {{error}}",
    finalQueueFailed: "Couldn't queue final render · {{error}}",
    // CF-H2: render reached before the clip is set up (no scene plan yet)
    preflightNoScenePlanTitle: "Clip not set up yet",
    preflightNoScenePlanBody:
      "Pick a template, voice & music before rendering.",
    preflightSetupCta: "Set up clip",
    scenePlanRedirect: "No clip setup yet — opening the Editor to set it up first.",
    // CF-Nit: cancel toasts (localized; no false "quota refunded" claim)
    cancelledToast: "Render cancelled",
    cancelFailToast: "Couldn't cancel · {{error}}",
    // CF-H4: client render timeout (taking longer than the worker ETA)
    stuckTitle: "Longer than expected",
    stuckBody:
      "This render is taking unusually long. Try refreshing, or cancel and re-render.",
    stuckRefreshCta: "Refresh",
    openPreviewUrl: "Open preview URL",
    ctaNextExport: "Next → Export",
    bugReportAck: "Bug report received · the iSale team will reply within 24h",
    errorDetailLabel: "ERROR DETAILS",
    errorSessionLine: "Session: {{session}} · {{time}} · logged for the team",
    fallbackTryOther: "Try another provider",
    fallbackEta: "{{provider}} · ~{{eta}}s",
    fallbackRetryLabel: "Retry {{label}}",
    fallbackRetryMeta: "~50s · free",
    bugReportTitle: "Report a bug to the iSale team",
    bugReportMeta: "Reply within 24h",
  },
  modals: {
    upgradeTitle: "Monthly quota exhausted",
    upgradeBody: "Upgrade to Starter for 30 more clips/month.",
    upgradeCta: "Upgrade · See plans",
    upgradeLater: "Wait for next month",
    costCapTitle: "AI cost cap reached",
    costCapBody: "You've used this month's AI budget. Upgrade to keep creating.",
    costCapCta: "Upgrade · See plans",
    costCapLater: "Maybe later",
    logoutTitle: "Sign out?",
    logoutBody: "You'll need to sign in again to make clips.",
    deleteAccountTitle: "Delete account permanently",
    deleteAccountBody: "All products, clips, and audit logs will be deleted after 30 days.",
    deleteAccountConfirmPhrase: "Delete my account permanently",
    cancelRenderTitle: "Cancel render?",
    cancelRenderCta: "Cancel · no quota used",
    cancelRenderWait: "Keep waiting",
    shareTitle: "Share clip",
    shareSubtitle: "Open the native share sheet, or copy the URL",
    shareTikTokSubtitle: "Open TikTok with the MP4 + caption",
    shareFacebookLabel: "Facebook",
    shareFacebookSubtitle: "Share to Reels / Story",
    shareInstagramLabel: "Instagram",
    shareInstagramSubtitle: "Share to Reels",
    shareYoutubeLabel: "YouTube Shorts",
    shareYoutubeSubtitle: "Share as a 9:16 vertical Short",
    shareShopeeLabel: "Shopee Video",
    shareShopeeSubtitle: "Upload as product video in your shop",
    shareCopyUrlLabel: "Copy URL",
    shareSheetOpened: "Share sheet opened",
    shareSheetFailed: "Couldn't open share sheet",
    shareSuccess: "Clip shared",
    shareFailed: "Share failed",
    logoutCta: "Sign out",
    cancelRenderBodyTitle: "Already at {{pct}}% — almost done!",
    cancelRenderBodyDesc:
      "Cancelling won't use any quota (it's only charged when a render finishes). If it finishes but you don't watch right away, no extra cost.",
    deleteProductTitle: "Delete {{name}}?",
    deleteProductBody:
      "Photos, facts, and all related clips will be deleted. This cannot be undone.",
    deleteProductConfirm: "Delete permanently",
    // GA-X-04: Archive (soft-delete) product
    archiveProductTitle: "Archive {{name}}?",
    archiveProductBody:
      "The product will be hidden from your list. Photos and facts are preserved — you can restore it from Settings.",
    archiveProductConfirm: "Archive",
    archiveProductCancel: "Cancel",
    archiveProductSuccess: "Product archived",
    archiveProductCta: "Archive product",
    voicePickerTitle: "Pick a voice",
    voicePickerSubtitle: "Tap ▶ to preview before selecting.",
    voicePickerUseCta: "Use this voice",
    voicePickerPreviewA11y: "Preview voice {{name}}",
    voicePickerProBadge: "Pro",
    complianceTitle: "Some claims need a second look",
    complianceBody:
      "The lines below may violate ad policy. Fix or remove them before generating the clip.",
    complianceBackToFacts: "Back to Fact Locker",
    complianceGuidance:
      'Tip: use softer wording ("helps cleanse") rather than absolute claims ("cures 100%").',
    deleteAccountConfirmCta: "Delete account",
    deleteAccountUnlockCta: "Type the phrase to unlock",
    deleteAccountTypePrompt: "To confirm, type exactly:",
    deleteAccountTypePlaceholder: "Type the phrase…",
    deleteAccountRecoverHint:
      "You have 30 days after deletion to recover by signing in again.",
    tierBadgeCurrent: "Current",
    tierBadgePopular: "Popular",
    tierVideosPerMonth: "{{n}} videos/mo",
  },
  intl: {
    today: "Today",
    yesterday: "Yesterday",
    earlier: "Earlier",
  },
  tabs: {
    home: "Home",
    products: "Products",
    clips: "Clips",
    account: "Me",
  },
  onboarding: {
    skip: "Skip",
    continue: "Continue",
    start: "Get started",
    step: "Step {{n}}/{{total}}",
    valueTitle: "AI sales clips\nin 3 minutes",
    valueBody: "Snap 3-5 product photos — iSale writes the script, dubs in Vietnamese, and stitches a 9:16 clip ready for TikTok, Reels, Shorts & Shopee Video.",
    painTitle: "CapCut takes 2-3 hours?\n3 minutes here.",
    painBody: "Skip the learning curve. No editing skills required. AI handles script + voice + scene assembly — you only review + post.",
    howTitle: "3 steps · 1 tap",
    howBody: "Snap photos → AI analyses & writes the script → You pick a version and get the MP4. No 'render overnight' phase.",
    trustTitle: "Transparent + safe",
    trustBody: "Every AI call has an audit log. Compliance check runs automatically for cosmetics, supplements, pharma. You can erase your data anytime.",
    notifTitle: "Allow notifications?",
    notifBody: "We'll ping you when your clip is done (~30 seconds away!). No spam — only events tied to your account.",
    notifAllow: "Allow notifications",
    notifLater: "Maybe later",
  },
  notificationsInbox: {
    title: "Notifications",
    markAllRead: "Mark all as read",
    empty: "No notifications yet",
    badgeRenderDone: "Rendered",
    badgeRenderFailed: "Render failed",
    badgeQuotaWarn: "Quota",
    badgePaymentFailed: "Payment",
    justNow: "just now",
    minutesAgo: "{{n}} min ago",
    hoursAgo: "{{n}} hr ago",
  },
  profileOnboarding: {
    menuRowTitle: "Walk-through again",
    menuRowNote: "5-step tour of how iSale works",
    card1Title: "Snap product photos",
    card1Body: "1-5 photos, JPG/PNG/WebP. iSale auto-moderates and AI extracts selling points.",
    card2Title: "Verify the Fact Locker",
    card2Body: "Tick ✓ on the facts AI finds. ≥3 verified unlocks script gen — keeps AI honest.",
    card3Title: "AI writes 3 scripts",
    card3Body: "AI drafts hook + scenes + CTA. Pick one and tweak as needed.",
    card4Title: "Fast editor + render",
    card4Body: "Pick template + voice + music. Preview render 60s, final ~3 minutes.",
    card5Title: "Download MP4 + share clip",
    card5Body: "Export 1080×1920 + caption + hashtags. Post to TikTok, Reels, Shorts & Shopee Video.",
    startCta: "Get started",
    nextCta: "Next →",
    skipCta: "Skip",
    stepLabel: "Step {{n}}/5",
    doneCta: "Got it · Close",
  },
  // Auth-expired modal — replaces technical "Missing or malformed
  // Authorization header" toast with a friendly UX. Vincent 2026-05-23.
  auth: {
    sessionExpiredTitle: "Your session has expired",
    sessionExpiredSubtitle: "For security, please sign in again to continue.",
    sessionExpiredBody:
      "To keep your account safe, iSale signs you out after a period of inactivity. Any draft you were typing has been saved on this device.",
    sessionExpiredCountdown: "Redirecting in {{seconds}} second(s)…",
    sessionExpiredLoginNowCta: "Sign in now",
    sessionExpiredLaterCta: "Not now",
  },
  consent: {
    loading: "Loading AI terms…",
    loadFailedTitle: "Couldn't load AI terms",
    loadFailedBody: "Check your connection and try again.",
    retryCta: "Try again",
    saveFailed: "Couldn't save terms · {{error}}",
    tryAgainLater: "Please try again later",
    heroTitle: "iSale uses AI\nto make your clips",
    heroSubtitle: "Here's what we do with your data",
    sendsTitle: "We send to",
    sendsOnly: "Only sent:",
    sendsOnlyValue: "product photos + the info you typed",
    neverTitle: "NEVER sent",
    neverValue: "Email · payments · other workspaces · personal photos",
    policyLink: "Read full AI transparency policy",
    versionFooter: "Consent v{{version}} · audit log retained {{days}} days",
    acceptCta: "I agree · Make my clip",
    accepting: "Saving…",
    // §6 B1 — reprompt modal strings, fired when backend returns 403
    // CONSENT_REQUIRED indicating the consent policy version changed.
    repromptTitle: "AI terms updated",
    repromptSubtitle: "Version {{version}} — please re-acknowledge to keep using AI features.",
    repromptAcceptCta: "Agree & continue",
    repromptViewCta: "View details",
    repromptWhatsNewTitle: "What's new?",
    repromptWhatsNewBody:
      "Voice generation storage policy added · OpenAI data categories updated · rendered clips kept up to 90 days (was 60).",
  },
  helpTooltip: {
    acknowledgeCta: "Got it",
    reopenHint: "Re-open this tour anytime from Settings → Help.",
    homeTitle: "Home · 30-second tour",
    homeStep1: "Tap the big centre button to make a new clip.",
    homeStep2: "The quota chip shows videos remaining this month.",
    homeStep3: "'Recent clips' is where you replay rendered videos.",
    editorTitle: "Editor · 30 seconds",
    editorStep1: "Each scene on the timeline = a short segment of the clip.",
    editorStep2: "Tap an image / text to edit or reorder.",
    editorStep3: "The 'Change voice' button opens the free voice picker.",
    factsTitle: "Fact Locker · 30 seconds",
    factsStep1: "AI suggests selling points from photos + product name.",
    factsStep2:
      "Uncheck facts you don't agree with — AI won't use them in the script.",
    factsStep3:
      "Flagged facts (yellow) must be fixed or removed before continuing.",
    renderTitle: "Rendering · 30 seconds",
    renderStep1: "Renders take 1-3 minutes depending on length + quality.",
    renderStep2: "You can close the app — iSale will notify you when done.",
    renderStep3:
      "If a render runs > 5 minutes, you can cancel and retry for free.",
  },
  emptyStates: {
    productsTitle: "No products yet",
    productsBody:
      "Add your first product — snap a few photos and iSale will suggest selling facts.",
    productsCta: "Add product",
    clipsTitle: "No clips yet",
    clipsBody:
      "Once renders finish, your clips will appear here to review and share.",
    clipsCta: "Make your first clip",
    searchInitialTitle: "Start searching",
    searchInitialBody:
      "Type a product, fact, or clip name to quickly find it in your workspace.",
    searchNoResultsTitle: 'No results for "{{query}}"',
    searchNoResultsBody: "Try other keywords or remove filters to broaden results.",
    notificationsTitle: "No notifications yet",
    notificationsBody:
      "iSale will notify you when a render finishes or there's a new selling tip.",
    notificationsCta: "Back to home",
    aiLogTitle: "No AI activity yet",
    aiLogBody:
      "When iSale calls AI for you (fact suggestions, script, voice), each call appears here.",
    aiLogCta: "Make your first clip",
  },
  emptyHome: {
    greeting: "Hello newcomer,",
    name: "you 👋",
    title: "No clips yet",
    subtitle: "Snap one product photo — 3 minutes later you've got a selling clip.",
    step1: "Snap product photos",
    step1Note: "3–5 angles",
    step2: "Confirm what AI inferred",
    step2Note: "~30 seconds",
    step3: "Get your clip · post anywhere",
    step3Note: "~2 minutes",
    cta: "+ Make first clip",
    watchVideo: "Or watch the 60s demo ↗",
  },
  products: {
    // GA-M-05 hardcode sweep (2026-06-05)
    cameraPermissionError: "Camera permission · {{error}}",
    libraryPermissionError: "Photo library permission · {{error}}",
    uploadError: "Upload failed · {{error}}",
    retryLaterHint: "Please try again later",
    analyzeNeedImage: "Need at least 1 moderation-approved photo to run AI analysis.",
    analyzeDone: "Analysis done · {{created}} new facts ({{total}} total) · {{flags}}",
    analyzeFailed: "Analysis failed · {{error}}",
    retryHint: "Please try again",
    title: "Products",
    heroTitle: "Product photo library",
    heroBody: "Each product holds photos + verified facts → spin up a clip anytime.",
    emptyTitle: "No products yet",
    emptyBody: "Add a product so the AI remembers its name, price and details — reuse it any time.",
    emptyCta: "+ Add product",
    emptyCaptureCta: "Or snap a photo now",
    searchPlaceholder: "Search products…",
    searchA11y: "Search products",
    statusReady: "Ready",
    statusDraft: "Draft",
    gridSubtitle: "{{count}} products · {{ready}} ready to make clips",
    gridAddCta: "Add product",
    noImage: "No photo",
    searchEmpty: "No matching products",
    detailTitle: "Product",
    detailAnalyzeTitle: "Analyse with AI",
    detailAnalyzeBody: "AI reads the images, proposes selling points + audience + claims. Verify them before using to generate the script (Fact Locker).",
    detailAnalyzeCta: "Analyse now",
    /** GA-M-13: moved from productsWeb namespace — used on both mobile + web. */
    detailCreateClipCta: "Create clip from this product",
    detailVerifyFactsCta: "Verify facts",
    detailAnalyzeBusy: "Analysing…",
    detailAnalyzeCost: "Free (cached)",
    detailAssetsLabel: "Photos",
    detailAssetsEmpty: "No photos yet.",
    detailEditTitle: "Edit name & category",
    detailEditBody: "Update product name and category",
    newTitle: "New product",
    newStep1Kicker: "Step 1 / 3",
    newStep1Title: "Product info",
    newStep1Body: "Use a short name (e.g. \"SPF50 Sunscreen\"). Category tells AI which compliance rules to apply.",
    newNameLabel: "Product name",
    newNamePlaceholder: "e.g. SPF50 Sunscreen",
    newCategoryLabel: "Category",
    newStep2Kicker: "Step 2 / 3",
    newStep2Title: "Product photos",
    newStep2Body: "Capture or pick 1-{{max}} photos. JPG/PNG/WebP, ≤ 5MB each. Clean + bright photos help AI read accurately.",
    newFramingTipTitle: "Frame the product in the center",
    newFramingTipBody: "3–5 angles · clean background · enough light",
    newCameraCta: "Capture",
    newLibraryCta: "From library",
    newAssetsCount: "{{n}}/{{max}} photos",
    newAssetsEmpty: "No photos yet. Use the buttons above to add some.",
    newSubmitCta: "Create product + upload {{n}} photos",
    newSubmitBusy: "Creating product…",
    newDoneTitle: "Product created",
    newDoneApproved: "Approved",
    newDoneRejected: "Rejected",
    newDoneFailed: "Upload errors",
    newDoneRejectedHint: "{{n}} photos rejected by moderation. Open the detail page to replace or remove them.",
    newDoneDetail: "View detail + AI analysis",
    newDoneBack: "Back to list",
    catCosmetic: "Cosmetics",
    catSupplement: "Supplements",
    catFashion: "Fashion",
    catFood: "Food",
    catElectronics: "Electronics",
    catHome: "Home goods",
    catOther: "Other",
    uploadInitializing: "Initializing…",
    uploadHashing: "Preparing photo {{index}}/{{n}}",
    uploadConnecting: "Connecting to server…",
    uploadUploading: "Uploading photo {{index}}/{{n}} · {{pct}}%",
    uploadConfirming: "AI is analyzing {{n}} photos… · ~{{etaS}}s left",
    uploadFailed: "Photo {{index}} failed: {{error}}",
    uploadProcessing: "Processing image…",
    validateNameLen: "Name must be 2-200 chars",
    editSaveSuccess: "Changes saved",
    editDeleteSuccess: "Product deleted",
    detailFallbackName: "this product",
    editTitle: "Edit product",
    editDirty: "Unsaved",
    editSaveCta: "Save changes",
    editSaveBusy: "Saving…",
    editSaveNoChange: "No changes",
    editDangerTitle: "Danger zone",
    editDangerCard: "Delete product",
    editDangerBody: "Photos, facts, and related clips will be archived (30-day soft-delete). You can restore from Settings.",
    editDangerCta: "Delete this product",
  },
  projects: {
    listTitle: "Clips",
    listHeroTitle: "Your clips",
    listHeroBody: "All clips you've rendered — share the clip, re-download MP4, or render again with a different template/voice.",
    listLoading: "Loading clips…",
    listEmptyTitle: "No clips yet",
    listEmptyBody: "Make your first clip — the AI learns the style you like.",
    filterAll: "All",
    filterA11y: "Filter clips",
    searchPlaceholder: "Search clips…",
    searchA11y: "Search clips",
    filterInProgress: "In progress",
    sectionInProgress: "In progress",
    sectionDone: "Done",
    sectionArchived: "Archived",
    stepScript: "Script",
    stepRender: "Render",
    stepExport: "Export",
    continueCta: "Continue →",
    filterEmpty: "No clips with this status",
    listEmptyCta: "+ Make clip",
    newTitle: "New clip",
    newKicker: "Last step before script generation",
    newHero: "Name + pick a length",
    newBody: "AI will write 3 script versions matching the tone + duration.",
    newNameLabel: "Clip name",
    newNamePlaceholder: "e.g. SPF50 morning-routine demo",
    newDurationLabel: "Duration (seconds)",
    newToneLabel: "Script tone",
    newSubmitCta: "Create + generate 3 scripts",
    newSubmitBusy: "Creating + generating…",
    newCostHint: "AI generates 3 script versions · ~10 seconds",
    toneCasual: "Friendly",
    toneProfessional: "Professional",
    tonePlayful: "Playful",
    toneTrustworthy: "Trustworthy",
    validateNameLen: "Name must be 2-200 chars",
    scriptTitle: "Script",
    scriptEmptyTitle: "No script yet",
    scriptEmptyBody: "AI will generate 3 script versions from your verified Facts. Pick the best one to continue.",
    scriptGenerateCta: "Generate script with AI",
    scriptGenerating: "AI is writing your script…",
    scriptGeneratingBody: "This takes about 10–20 seconds. Please don't close the app.",
    scriptGenerateFail: "Script generation failed. Try again?",
    scriptLoading: "Loading scripts…",
    scriptComplianceWarn: "{{n}} compliance warnings",
    scriptComplianceBody: "Read flagged lines carefully; if they contain violations tap \"Regenerate\" with specific feedback.",
    scriptRegenCta: "Regenerate with feedback…",
    scriptRegenTitle: "Feedback for the next generation",
    scriptRegenBody: "e.g. \"more energetic\", \"shorter to 20s\", \"don't use 'whitening'\".",
    scriptRegenPlaceholder: "Leave empty = random regenerate",
    scriptRegenSubmit: "Regenerate 3 versions",
    scriptRegenBusy: "Regenerating script…",
    scriptSelectBusy: "Saving…",
    scriptSelectActive: "Selected ✓",
    scriptSelectCta: "Pick this version",
    scriptCtaPick: "Pick a version to continue",
    scriptCtaNext: "Next → Editor + Render",
    scriptCaptionBlock: "Caption + hashtags (auto)",
    scriptCaptionLoading: "Generating caption…",
    scriptCaptionEmpty: "Caption appears after you pick a version.",
    scriptSelectedBadge: "★ SELECTED",
    scriptToneEnergetic: "Energetic",
    scriptToneRefined: "Refined",
    scriptToneFun: "Playful",
    scriptSelectFailed: "Couldn't select · {{error}}",
    scriptRegenSuccess: "Generated 3 new variants",
    scriptRegenFailed: "Couldn't regenerate · {{error}}",
    scriptVariantMeta: "{{seconds}}s · {{chars}} chars",
    scriptMoreScenes: "+ {{n}} more scenes",
  },
  editor: {
    title: "Editor Lite",
    scenePillSummary: "{{scenes}} scenes · {{seconds}}s",
    tabTemplate: "Template",
    tabVoice: "Voice",
    tabMusic: "Music",
    tabSubtitle: "Subtitle",
    templateBody: "Aspect 9:16 for every short-video platform",
    voiceCount: "{{n}} Vietnamese Neural voices",
    voiceWarn: "⚠ Changing voice = re-TTS all scenes (new POST).",
    musicCount: "{{n}} license-cleared tracks",
    musicNone: "(No background music)",
    musicNoneSub: "Voice-over only",
    subtitleBody: "Subtitle styling (recommended: Noto Sans VN)",
    engineLabel: "Render engine",
    engineFast: "Fast Mode",
    engineFastNote: "Free · ~3s render · Ken Burns + TTS",
    engineAi: "AI Mode",
    engineAiNote: "AI hero scene · Phase 2 PRO",
    planSummary: "Current storyboard",
    planSummaryBody: "{{scenes}} scenes · {{seconds}}s total · {{cacheHits}} cache-hit scenes (free)",
    saveCta: "Build storyboard + render →",
    saveUpdateCta: "Save changes + render →",
    saveBusy: "Saving storyboard…",
    missingInfo: "Pick template + voice before rendering.",
    subtitleStyleWhite: "Basic white",
    subtitleStyleYellow: "Bold yellow",
    voiceGenderFemale: "Female",
    voiceGenderMale: "Male",
    voicePreviewA11y: "Open voice preview",
    voicePreviewCta: "Preview",
    voicePreviewPreparing: "Preparing preview of {{name}}…",
  },
  export: {
    title: "Export + Share",
    heroTitle: "Your clip is ready 🎉",
    heroPreviewLabel: "9:16 preview",
    heroMeta: "MP4 · 9:16 · session {{session}}",
    readyKicker: "Ready to post",
    readyTitle: "Download + share",
    watermarkTitle: "iSale watermark is on",
    watermarkBody: "Free-tier renders carry the watermark. Upgrade to Pro to remove.",
    captionLabel: "Caption + hashtags",
    platformLabel: "Target platform",
    platformHint: "Hashtags auto-tune per platform — no AI re-gen",
    copyForPrefix: "Copy for",
    captionLoading: "Creating caption + hashtags…",
    captionEmpty: "—",
    formatsLabel: "Formats",
    formatMp4: "MP4",
    formatThumbnail: "Thumbnail",
    formatSrt: "SRT",
    formatAll: "All",
    existingLabel: "Exported ({{n}})",
    existingEmpty: "Tap a button above to export.",
    shareCta: "Share clip",
    shareHint: "Opens native share sheet · caption + hashtags + URL pre-filled",
    actionView: "Preview",
    actionEdit: "Edit",
    actionRerender: "Re-render",
    actionExport: "Export",
  },
  aiActivity: {
    title: "AI activity log",
    intro: "Every AI call we make for you is logged — transparent & you can request deletion.",
    filterAll: "All",
    loading: "Loading audit log…",
    emptyAll: "No audit log yet.",
    emptyProvider: "No {{provider}} calls yet.",
    sectionToday: "TODAY",
    sectionYesterday: "YESTERDAY",
    sectionEarlier: "EARLIER",
    capAnalyzeImage: "Analyse image",
    capGenerateScript: "Generate script",
    capGenerateCaption: "Caption + hashtags",
    capCheckCompliance: "Compliance check",
    capSynthesizeVoice: "Voice-over",
    capGenerateVideo: "Video assembly",
    rowProviderLabel: "AI system:",
    rowSessionLabel: "session",
    rowTokensUnit: "tokens",
    rowLatencyUnit: "ms",
    loadMore: "Load more",
    loadingMore: "Loading more…",
    showingOf: "Showing {{shown}} of {{total}}",
  },
  profileEdit: {
    title: "Edit profile",
    dirty: "Unsaved",
    changeAvatar: "Change avatar",
    avatarHint: "Coming soon — change your profile photo in an upcoming update.",
    nameLabel: "Display name",
    namePlaceholder: "Name shown in the app",
    emailLabel: "Email (read-only)",
    emailHint: "Changing email = signing in with a different account.",
    localeLabel: "Language",
    localeHint: "Save also propagates to Stripe checkout + audit log.",
    saveCta: "Save changes",
    saveShort: "Save",
    saveBusy: "Saving…",
    saveNoChange: "No changes",
    loading: "Loading profile…",
    loadError: "Could not load profile",
    saved: "Profile saved",
  },
  themePicker: {
    title: "Appearance",
    intro: "Pick the theme mode. \"System\" follows your OS — light during the day, dark when you toggle dark mode on iOS / Android.",
    lightLabel: "Light mode",
    lightDesc: "White surface · dark text · daytime use",
    darkLabel: "Dark mode",
    darkDesc: "Dark surface · light text · easier on the eyes at night",
    systemLabel: "Follow system",
    systemDesc: "Theme follows OS · vi/en/zh all supported",
    betaKicker: "BETA",
    betaBody: "A few older screens don't follow the theme yet. We're rolling it out so the whole app stays consistent.",
    toastSystem: "Follow system",
    toastDark: "Dark mode",
    toastLight: "Light mode",
  },
  languagePicker: {
    // GA-M-05 sweep (2026-06-05)
    syncFailed: "Language changed on this device; not yet synced to the server ({{error}})",
    title: "Language",
    intro: "Pick the display language. Vietnamese is the default. English and Chinese are fully translated.",
    parityLabel: "Glossary parity",
    parityBody: "All keys exist in 3 locales. Currency auto-switches VND ↔ USD ↔ CNY per locale. Date format follows Intl API.",
    restartTitle: "App restart required",
    restartToZh: "Switching to Chinese requires reloading native modules.",
    restartFromZh: "Switching away from Chinese requires reloading native modules.",
    restartCta: "Change & remind me to restart",
    restartConfirmed: "Language changed · please force-quit and reopen the app to finish",
    toastSwitched: "Switched to {{name}}",
    nativeViet: "Xin chào!",
    nativeEn: "Hello!",
    nativeZh: "你好!",
    requiresRestartSuffix: "· needs app restart",
  },
  dashboard: {
    kicker: "Overview",
    newClipCta: "New clip",
    quotaLabel: "THIS MONTH",
    quotaRemaining: "Plan {{plan}} · {{n}} clips left",
    upgradePro: "Upgrade to Pro →",
    backendDown: "Can't reach the iSale server. Check your connection and reload.",
    tipsKicker: "QUICK TIP",
    tipsBody:
      "Each product should have 3-5 high-quality photos + you verify ≥ 3 facts before script generation. AI writes more accurate scripts when facts are verified.",
    emptyProducts: "No products yet — visit Products to create one.",
    emptyClips: "No clips yet — visit Clips to generate.",
    openLink: "Open",
    seeAll: "See all →",
  },
  notifications: {
    bellLabel: "Notifications",
    drawerTitle: "Notifications · {{count}} new",
    readAll: "Mark all read",
    loading: "Loading…",
    loadError: "Could not load notifications",
    emptyTitle: "Inbox is empty",
    emptyBody: "We'll ping you here when a clip renders or there's an update.",
    relativeNow: "just now",
    relativeMin: "{{n}}m",
    relativeHour: "{{n}}h",
    relativeDay: "{{n}}d",
  },
  upload: {
    /** Screen-reader-only moderation badge labels — see vi.ts comment. */
    phase_approved: "Photo approved",
    phase_pending: "Photo pending review",
    phase_rejected: "Photo rejected",
    idleTitle: "Drag product photos here",
    idleSubline: "Or click to choose from device · JPG / PNG / WebP · up to 10 photos",
    dragHere: "Drop photos here",
    rejectTitle: "This file is not supported",
    rejectSubline: "'{{file}}' was rejected · {{reason}}",
    reasonType: "unsupported format",
    reasonSize: "exceeds 5MB limit",
    hintClick: "Click to choose",
    hintPaste: "Cmd+V to paste",
    hintFolder: "Drag a folder",
    or: "or",
    pickFromDevice: "Choose from device",
    pickFolder: "Upload folder",
    pasteCta: "Paste from clipboard",
    tipKicker: "Tip",
    tipBody: "3–5 photos from multiple angles help the AI analyze more accurately.",
    tooManyFiles: "Selected {{selected}} photos but only {{room}} slots remain · extras skipped",
    // Generic — raw {{error}} removed (see vi.ts for context).
    prepareFail: "Couldn't prepare the photo. Please try again.",
    preparing: "Preparing photos…",
    headerCount: "{{count}}/10 photos · {{size}}",
    headerMax: "{{count}}/10 photos · {{size}} · max reached",
    headerUploading: "Uploading {{done}}/{{total}} · {{pct}}% overall",
    clearAll: "Clear all",
    continue: "Continue",
    cancelAll: "Cancel all",
    addMore: "Add more",
    maxTip: "Tip: 5–7 photos is usually enough for the AI — no need for 10",
    errorTitle: "{{count}} photo(s) failed to upload",
    errorGeneric: "Upload failed",
    retryAll: "Retry all",
    removeFile: "Remove",
    rejectedOpen: "View moderation detail",
    rejectedChip: "Policy violation",
    moderating: "Moderating",
    confirmTitle: "Confirm upload",
    confirmStart: "Start upload",
    confirmSummary: "{{count}} photos · {{size}} · ready to send",
    confirmAiHint: "After upload, AI will analyze — you can edit before generating the script.",
    kbdHint: "Press Esc to cancel · Enter to confirm",
    cancelTitle: "Cancel upload?",
    cancelKeepGoing: "Keep uploading",
    cancelDoIt: "Cancel upload",
    cancelBody: "{{done}}/{{total}} photos done. Cancelling will discard everything and return to dropzone.",
    pasteTitle: "Paste image from clipboard",
    pasteAdd: "Add to list",
    // Generic failure message — 401 handled by AuthExpiredModal; this
    // covers network glitch / 5xx with non-technical copy.
    urlsFail: "Couldn't prepare the upload. Please check your network and retry.",
    completeTitle: "Upload complete!",
    completeBody: "{{count}} photos are ready. AI can start analysis.",
    completeRejectedNote: "{{count}} photo(s) were rejected by moderation and not included.",
    completeEta: "Analysis takes ~15 seconds · doesn't count against quota",
    // Renamed — see vi.ts comment.
    completeAnalyze: "Done, view product",
    completeAddMore: "Upload more photos",
    rejectedAllTitle: "All photos rejected",
    rejectedAllBody: "At least 1 photo must pass moderation for AI to analyze the product.",
    rejectedAllReasonLabel: "Moderation reasons",
    rejectedAllHint: "Try clearer product photos · avoid photos with people's faces or unsuitable content.",
    rejectedAllRetry: "Upload other photos",
    moderationDrawerTitle: "Photo failed moderation",
    moderationReasonLabel: "Reason",
    moderationDefaultReason: "AI detected inappropriate content",
    moderationPolicyLink: "View moderation policy",
    moderationDelete: "Remove photo",
    // Friendly reject-reason mapping — see vi.ts for full context.
    rejectReasons: {
      systemLicense_title: "Photo awaiting review",
      systemLicense_body: "The moderation AI model isn't fully enabled on this environment yet. This is a system issue, not a problem with your photo.",
      systemLicense_hint: "Please try again later or contact your admin if this persists.",
      systemAi_title: "Moderation temporarily unavailable",
      systemAi_body: "The AI moderation service isn't responding correctly right now. Your photo is fine.",
      systemAi_hint: "Please retry in a few minutes.",
      systemR2_title: "Couldn't read photo from server",
      systemR2_body: "Failed to read the uploaded photo back from storage. Usually a partial upload or network glitch.",
      systemR2_hint: "Please re-upload the photo.",
      unparseable_title: "Invalid moderation result",
      unparseable_body: "The AI returned malformed output. This is a system bug.",
      unparseable_hint: "Please retry.",
      abandoned_title: "Upload was interrupted",
      abandoned_body: "A previous upload didn't finish (app/browser closed early).",
      abandoned_hint: "Please re-upload the photo.",
      size_title: "Photo too large",
      size_body: "Photo exceeds the allowed size limit (max 10MB).",
      size_hint: "Compress or pick a smaller file.",
      content_title: "Inappropriate content",
      content_body: "{{detail}}",
      content_hint: "Please use a different product photo. Avoid sensitive content.",
      unknown_title: "Rejected",
      unknown_body: "The AI didn't provide a specific reason.",
      technicalDetailLabel: "Technical detail",
      technicalDetailHint: "For dev / support escalation",
      groupCountSuffix: "{{count}} photos",
    },
  },
  productsWeb: {
    createCta: "Create product",
    uploadFor: "Upload product photos",
    createTitle: "New product",
    createSubmit: "Create",
    createBusy: "Creating product…",
    createFailPrefix: "Could not create:",
    uploadHint:
      "Upload photos in the mobile app — the web version doesn't support drag-and-drop yet.",
    tableName: "Name",
    tableCategory: "Category",
    tableStatus: "Status",
    tableUpdated: "Updated",
    // GA-W-12: was `emptyHintMobile` — renamed (no Mobile suffix; used on web route only)
    emptyHint:
      'Click "Create product" to start, or use the mobile app to upload photos + facts.',
    cardCategory: "Category",
    cardUpdated: "Updated {{date}}",
    cardNoCover: "No photo yet",
    cardOpen: "Open",
    detailBackToList: "Back to Products",
    detailBreadcrumb: "Products",
    detailAssetsTitle: "Product photos",
    detailAssetsEmpty: "No photos yet — upload below to get started.",
    detailUploadTitle: "Upload photos",
    detailUploadMore: "Upload more",
    detailCreateClipCta: "Create clip from this product",
    detailVerifyFactsCta: "Verify facts",
    detailNotReady: "Need 3 approved photos to create a clip.",
    detailLoading: "Loading product…",
    detailLoadError: "Could not load product",
    detailRetry: "Try again",
    detailAuthErrorTitle: "Session expired",
    detailAuthErrorCta: "Sign in again",
    detailJoinedAt: "Created {{date}}",
    detailAssetCountReady: "{{n}} ready",
    detailAssetCountPending: "{{n}} pending",
    detailAssetCountRejected: "{{n}} rejected",
    // EP-7: template-context banner on product list
    templateBannerUsing: "Template selected:",
    templateBannerPickHint: "Pick a product below to start creating a clip with this template.",
    templateBannerClear: "Clear template",
  },
  projectsWeb: {
    // GA-W-12: was `mobileHint` — renamed; was `emptyHintMobile` — renamed
    featureHint:
      "Edit scripts, swap voice/music/template, render preview or final, and export MP4 / SRT / caption files.",
    emptyHint: "No clips yet. Click \"New clip\" to start from a ready product.",
    cardOpen: "Open",
    cardCreated: "Created {{date}}",
    newCta: "New clip",
    newModalTitle: "Create a new clip",
    newProductLabel: "Pick a product",
    newProductPlaceholder: "— Pick a ready product —",
    newNoReadyProducts: "No products in Ready status yet. Upload images + verify facts first.",
    newSubmitting: "Creating clip…",
    newSubmitFail: "Could not create clip · {{error}}",
    newSubmitHint: "Select a product and enter a name to create",
    goToProducts: "Go to Products",
  },
  billingWeb: {
    currentLabel: "Current",
    tierName: "{{name}} plan",
    resetCost: "Resets {{date}} · cost ${{used}} / ${{cap}}",
    starterFeature1: "30 videos/month",
    starterFeature2: "No watermark",
    starterFeature3: "5 premium VN voices",
    proFeature1: "150 videos / month",
    proFeature2: "AI hero scene (Kling)",
    proFeature3: "Brand kit + custom templates",
    enterpriseFeature1: "Unlimited videos",
    enterpriseFeature2: "API access · SLA",
    enterpriseFeature3: "Team workspace",
    enterpriseTagline: "For teams & large shops",
    enterprisePrice: "Contact us",
    perMonth: "/mo",
    perYear: "/yr",
    checkoutFail: "Could not start checkout:",
    freeFeature1: "3 videos / month",
    freeFeature2: "Fast Mode render (no Kling)",
    freeFeature3: "iSale watermark",
    lifetimeFeature1: "30 videos / month",
    lifetimeFeature2: "AI Mode (Kling i2v): 3 videos/month",
    lifetimeFeature3: "Priority support + beta features",
    lifetimeTagline: "Pay once, use forever",
    lifetimePeriod: "one-time",
    lifetimePrice: "2,990,000₫",
    emailIntentTitle: "Register Lifetime early",
    emailIntentSubtitle: "Limited to 200 people · We will reach out when payment opens.",
    emailIntentEmailPlaceholder: "your@email.com",
    emailIntentEmailLabel: "Email address",
    emailIntentCta: "Register",
    emailIntentSuccess: "Got it! We will be in touch soon.",
    emailIntentError: "Could not send — please try again.",
    emailIntentInvalid: "Invalid email address.",
  },
  aiActivityWeb: {
    heroTitle: "What we did for you, on the record",
    introFull:
      "Every time AI helps you (reading photos, writing scripts, synthesizing voice) — we log it here so you can audit. Nobody else sees your clips or products beyond the AI providers listed below. Logs auto-purge after 90 days.",
    pillSummary: "{{n}} AI actions",
    tableWhen: "When",
    tableCapability: "Capability",
    tableProviderModel: "AI system (model)",
    tableCost: "Cost",
    tableLatency: "Speed (ms)",
    tableStatus: "Status",
  },
  projectDetailWeb: {
    loadingProject: "Loading clip…",
    notFoundTitle: "Clip not found",
    backToClips: "Back to Clips",
    backAllClips: "All Clips",
    pillLabel: "Clip",
    productLabel: "Product:",
    productNone: "—",
    versionPill: "v{{version}}",
    emptyScriptTitle: "No script yet",
    emptyScriptBody:
      "AI will generate 3 script versions from your verified Facts. Pick the best one to continue.",
    generateScriptCta: "Generate script with AI",
    generatingScript: "AI is writing your script…",
    generatingScriptBody: "This takes about 10–20 seconds. Please don't close this page.",
    generateScriptFail: "Script generation failed. Try again?",
    complianceWarnCount: "{{n}} compliance warnings",
    complianceWarnBody:
      'Read flagged lines carefully; if they contain violations tap "Regenerate" with specific feedback.',
    providerFallback: "Unknown",
    modelFallback: "—",
    selectFailAlert: "Could not pick that version.",
    regenFailAlert: "Could not regenerate.",
    variantLabel: "Version {{n}}",
    variantMeta: "{{scenes}} scenes · {{seconds}}s",
    variantMoreScenes: "+ {{n}} more scenes",
    variantCtaLabel: "CTA",
    captionTitle: "Caption + hashtags (auto)",
    captionLoading: "Generating caption…",
    captionEmpty: "Caption appears after you pick a version.",
    regenCta: "Regenerate with feedback",
    regenBusy: "Regenerating…",
    ctaPick: "Pick a version to continue",
    ctaNext: "Next → Editor Lite",
    variantSelected: "Selected ✓",
    variantSelect: "Pick this version",
    variantSaveBusy: "Saving…",
    modalTitle: "Feedback for the next generation",
    modalBody: 'e.g. "more energetic", "shorter to 20s", "no \'whitening\'".',
    modalPlaceholder: "Leave empty = random regenerate",
    modalSubmit: "Regenerate 3 versions",
    modalClose: "Close",
  },
  editorWeb: {
    backToScript: "Back to script",
    heroTitle: "Pick voice · music · template",
    estCostBadge: "~${{cost}} est.",
    tabSubtitleEngine: "Subtitle + Engine",
    sectionTemplateTitle: "Aspect 9:16 for every short-video platform",
    sectionVoiceTitle: "{{n}} Vietnamese Neural voices",
    sectionVoiceWarn:
      "⚠ Changing voice = re-TTS all scenes (new POST, not PATCH).",
    sectionMusicTitle: "{{n}} license-cleared tracks",
    sectionMusicNoneTitle: "(No background music)",
    sectionMusicNoneSub: "Voice-over only",
    sectionSubtitleTitle: "Subtitle style",
    sectionEngineTitle: "Render engine",
    subtitleDefaultWhite: "Basic white",
    subtitleBoldYellow: "Bold yellow",
    engineFastTitle: "Fast Mode",
    engineFastBody:
      "Free · ~3s render · Ken Burns + TTS · no AI quota cost",
    engineAiTitle: "AI Mode",
    engineAiBody: "PRO · AI hero scene · Phase 2 (coming soon)",
    templateSubtitle: "{{aspect}} · transition: {{transition}}",
    voiceSubtitleFemale: "♀ · {{locale}}",
    voiceSubtitleMale: "♂ · {{locale}}",
    musicSubtitle: "{{mood}} · {{bpm}} BPM · {{seconds}}s",
    musicLicense: "License: {{license}}",
    planTitle: "Current storyboard",
    planBody:
      "{{scenes}} scenes · {{seconds}}s total · {{cacheHits}} cache-hit scenes (free)",
    saveBusy: "Saving storyboard…",
    saveUpdate: "Save changes + render →",
    saveCreate: "Build storyboard + render →",
    missingTemplateOrVoice: "Pick template + voice first.",
    needVariantFirst: "Pick a script variant before setting up the clip.",
    saveFailPrefix: "Could not save.",
    sectionLoading: "Loading options…",
  },
  renderWeb: {
    backToEditor: "Back to Editor Lite",
    heroTitle: "Preview or final?",
    heroBody:
      "Preview is free + fast but watermarked and low-res. Final uses quota + takes longer but is clean 1080×1920 (paid tier).",
    fastPreviewTitle: "Fast preview",
    fastPreviewBody: "~60-90s · iSale watermark · 720p · no quota cost",
    fastPreviewBadge: "FREE",
    fastPreviewCta: "Render preview",
    finalRenderTitle: "Final render",
    finalRenderBody: "~3 min · 1080×1920 · plan-tier watermark · 1 video used",
    finalRenderBadge: "QUOTA",
    finalRenderCta: "Render final",
    queueingBusy: "Preparing…",
    queuePreviewFail: "Couldn't start the preview clip",
    queueFinalFail: "Couldn't start the final clip",
    cancelFail: "Could not cancel",
    initJob: "Preparing…",
    pollingHint:
      "iSale is processing — feel free to close the app, we'll push when done.",
    stepLabel: "Step: {{step}}",
    stepFallback: "initialising",
    doneTitle: "Render done",
    doneBody: "Tap Next to export MP4 + thumbnail.",
    doneBodyEmbedded: "Download files & post — right below 👇",
    errorTitle: "Render error",
    errorUnknown: "Unknown error.",
    cancelledTitle: "Cancelled",
    detailsTitle: "Technical details",
    detailsToggleShow: "Show technical details",
    detailsToggleHide: "Hide details",
    rowId: "Render ID",
    rowType: "Type",
    rowStatus: "Status",
    rowProgress: "Progress",
    rowStep: "Step",
    rowWatermark: "Watermark",
    rowCdnUrl: "CDN URL",
    watermarkYes: "Yes",
    watermarkNo: "No",
    fallbackDash: "—",
    cancelCta: "Cancel render",
    retryCta: "↻ Try again",
    openPreviewUrl: "Open preview URL",
    copyUrl: "Copy link",
    nextExport: "Next → Export",
    // GA-W-09: preflight guard — no script yet
    preflightNoScriptTitle: "No script yet",
    preflightNoScriptBody:
      "The clip needs a script before it can be rendered. Go back to the Editor to generate one.",
    preflightGoToEditorCta: "Back to Editor",
    // CF-H2: render reached before the clip is set up (no scene plan yet)
    preflightNoScenePlanTitle: "Clip not set up yet",
    preflightNoScenePlanBody:
      "Pick a template, voice & music in the Editor before rendering.",
    scenePlanRedirect: "No clip setup yet — opening the Editor to set it up first.",
    // CF-H4: client render timeout (taking longer than the worker ETA)
    stuckTitle: "Longer than expected",
    stuckBody:
      "This render is taking unusually long. Try refreshing, or cancel and re-render.",
    stuckRefreshCta: "Refresh",
  },
  exportWeb: {
    backToRender: "Back to Render",
    pillLabel: "Export + Share",
    heroTitle: "Download + share",
    heroBody:
      "Ready for TikTok, Reels, Shorts & Shopee Video. Web copies URL + caption; mobile has the native share sheet.",
    watermarkTitle: "iSale watermark is on",
    watermarkBody: "Free-tier renders carry the watermark. Upgrade to Pro to remove.",
    captionTitle: "Caption + hashtags",
    platformLabel: "Target platform",
    platformHint: "Hashtags auto-tune per platform — no AI re-gen",
    copyForPrefix: "Copy for",
    captionLoading: "Creating caption + hashtags…",
    captionFallback: "—",
    formatsTitle: "Formats",
    attributionToggleTitle: 'Include "Made with iSale" ✨',
    attributionToggleNote:
      "Adds a single credit line when copying the caption. Helps others discover iSale through your clips.",
    attributionLine: "Made with iSale ✨ isale.app",
    celebrationTitle: "🎉 First clip done!",
    celebrationSubtitle: "Under 3 minutes — that's the iSale promise.",
    celebrationBody:
      "Post while the clip is still hot — the algorithm rewards fresh uploads. Copy the caption + URL below and post straight to TikTok, Reels, Shorts & Shopee Video — we've prepped the caption + hashtags for you.",
    celebrationCopyCta: "Copy caption + URL",
    celebrationUpgradeCta: "Remove watermark — Go Pro",
    celebrationDismissCta: "Later",
    noRenderHint: "No successful render for this clip yet.",
    goToRender: "Go to Render →",
    formatMp4: "MP4",
    formatThumbnail: "Thumbnail",
    formatSrt: "SRT",
    formatAll: "All",
    existingTitle: "Exported ({{n}})",
    existingEmpty: "Tap a button above to export.",
    rowSizeExpires: "{{kb}} KB · expires {{date}}",
    copyUrlAria: "Copy link",
    openFileAria: "Open file",
    openFileTitle: "Open / download",
    copySharePrefix: "Copy caption + URL to post",
    shareHint: "The link expires after 24 hours",
    copySuccess: "Caption + hashtags + URL copied",
    copyNeedMp4: 'No MP4 yet — tap "All" or "MP4" first.',
    exportFailPrefix: "Could not export.",
    /** FL-5: mobile export hardcode sweep. */
    exportNeedRenderFirst:
      "A successful render is required before exporting. Please go back to Render first.",
    exportSuccessCount: "Exported {{n}} file(s)",
  },
  settingsWeb: {
    deleteContactSubtitle:
      "Email privacy@isale.app · the team will action within 30 days per GDPR.",
    deleteUnderstood: "Got it",
    deleteContactToast: "Contact privacy@isale.app to delete your data",
    deleteRealSubtitle:
      "Your account will be disabled immediately. You have 30 days to restore before all data is permanently erased.",
    deleteRealBody:
      "After you confirm: all your sessions are signed out. Your products and clips stay recoverable for 30 days. After 30 days, all your products, clips, photos, and history are permanently erased per GDPR. No need to email support.",
    deleteConfirmCta: "Delete my account",
    deleteBusy: "Deleting…",
    deleteSuccessToast: "Account marked for deletion. You have 30 days to restore.",
    deleteFailPrefix: "Couldn't delete account",
  },
  themeWeb: {
    back: "Back to Settings",
    intro:
      'Pick an appearance mode. "System" follows your OS preference.',
    systemDesc:
      "Tracks your OS theme via prefers-color-scheme media query",
    betaKicker: "BETA",
    betaBody:
      "A few older pages don't follow the theme yet. We're rolling it out so the whole interface stays consistent.",
  },
  languageWeb: {
    back: "Back to Settings",
    intro:
      "Vietnamese is the default. EN + ZH glossary already translated — full-screen coverage coming after the i18n migration sweep.",
    requiresReloadSuffix: "· tab reload required",
    reloadTitle: "Tab reload required",
    reloadToZh:
      "Switching to Chinese requires a reload to load optimal CJK fonts.",
    reloadFromZh:
      "Switching away from Chinese requires a reload to free CJK fonts.",
    reloadCta: "Switch & reload",
    skipReloadCta: "Switch without reload",
    reloadToast: "Language switched · please reload the tab to finish.",
    localOnlyWarning:
      "Local switch OK; not yet synced with server ({{error}})",
  },
  oauthCallback: {
    working: "Finishing sign-in…",
    redirecting: "Redirecting…",
    errorMissing: "Missing data in Google response",
    errorWithCode: "Google rejected · {{code}}",
    errorExchange: "Could not exchange the session · {{error}}",
  },
  profileEditWeb: {
    back: "Back to Settings",
    saveFail: "Save failed · {{error}}",
    loadErrorFallback: "Check the backend connection.",
    avatarUploading: "Uploading…",
    avatarUploaded: "Avatar updated",
    avatarUploadFail: "Avatar upload failed · {{error}}",
    avatarTooLarge: "Image too large — max 5MB",
    breadcrumbRoot: "Settings",
    breadcrumbCurrent: "Profile",
    h1: "Personal profile",
    joinedAt: "Joined {{date}}",
    phoneLabel: "Phone",
    phonePlaceholder: "+84 ...",
    tiktokLabel: "Shop / social link",
    tiktokPlaceholder: "@your-handle",
    bioLabel: "Shop bio",
    bioPlaceholder: "Short description of your shop (max 200 chars).",
    avatarChipLabel: "Change avatar",
  },
  settingsSubnav: {
    kicker: "Settings",
    profile: "Profile",
    appearance: "Appearance",
    language: "Language",
    notifications: "Notifications",
    privacy: "Privacy & data",
    quota: "Quota & plan",
    aiActivity: "AI activity",
  },
  planAside: {
    kicker: "Current plan",
    planFree: "Free",
    planPro: "Pro",
    usageLine: "{{used}}/{{cap}} videos · {{remaining}} left",
    upgradeCta: "Upgrade to Pro",
    invoicesTitle: "3 recent invoices",
    invoiceLine: "{{date}} · ${{amount}} {{status}}",
    invoiceOk: "✓",
    invoiceFail: "✗",
  },
  notifPrefs: {
    breadcrumbCurrent: "Notifications",
    h1: "Notifications",
    groupRender: "Render & export",
    groupQuota: "Quota & plan",
    groupEmail: "Email",
    renderDone: "Clip render finished",
    renderFailed: "Render failed",
    renderReady: "Clip ready to download",
    quotaWarn: "20% quota remaining",
    quotaFull: "Out of monthly quota",
    quotaPromo: "Upgrade promos",
    emailWeekly: "Weekly summary",
    previewKicker: "Preview",
    previewPushHint: "📱 Push on phone",
    previewToastHint: "💬 In-app toast",
    previewAppName: "iSale",
    previewPushTitle: "✓ Clip \"Serum HA\" is ready",
    previewPushBody: "Tap to view · 30s · 9:16",
    previewToastBody: "Render done · 30s",
    previewFootnote: "Preview updates as you toggle each option.",
    previewDisabled: "OFF",
    saveBusy: "Saving…",
    savedLocal: "Saved notification settings (local)",
  },
  nav: {
    dashboard: "Dashboard",
    products: "Products",
    clips: "Clips",
    billing: "Plan",
    aiActivity: "AI Activity",
    settings: "Settings",
    workspace: "Workspace",
    signOut: "Sign out",
    signOutSuccess: "Signed out",
  },
  routes: {
    dashboard: "Dashboard",
    products: "Products",
    clips: "Clips",
    clipNew: "Create clip",
    projectScript: "Script",
    projectDetailParent: "Clip details",
    editor: "Editor Lite",
    render: "Render",
    export: "Export + Share",
    billing: "Plan",
    aiActivity: "AI Activity",
    settings: "Settings",
    settingsTheme: "Appearance",
    settingsLanguage: "Language",
    settingsNotifications: "Notifications",
    settingsOnboarding: "Replay onboarding",
    profileEdit: "Edit profile",
    productDetail: "Product detail",
    productFacts: "Verify facts",
    clipTemplates: "Clip templates",
    clipTemplateDetail: "Template detail",
  },
  // MP10 — Transactional email templates (5 templates × 3 locales)
  emails: {
    shared: {
      footer: "Automated email from iSale · no need to reply",
      helpLink: "Help",
      privacyLink: "Privacy",
      tosLink: "Terms",
    },
    welcome: {
      subject: "Welcome to iSale 🎉",
      preheader: "Your first selling clip in 3 minutes",
      heading: "Hi {{name}} 👋",
      body1:
        "Create selling clips from product photos with AI — no editing skills needed. Snap 3-5 photos and iSale handles the script, Vietnamese voiceover, and 9:16 cut ready for TikTok, Reels, Shorts & Shopee Video.",
      cta: "Start creating",
      freeTierNote: "You're on the Free plan — 5 clips/month included.",
    },
    renderDone: {
      subject: "Your clip for {{name}} is ready 🎬",
      preheader: "Open to preview — your render just finished",
      heading: "Clip for {{name}} is ready",
      body1:
        "Preview before you post — download the MP4 directly or share to TikTok, Reels, Shorts & Shopee Video from the link below.",
      meta: "{{duration}} · {{size}}",
      cta: "Watch clip",
      ctaSecondary: "Or open in the app:",
    },
    renderFailed: {
      subject: "Render for {{name}} didn't complete",
      preheader: "No quota charged — you can retry for free",
      heading: "Something hiccuped during render",
      body1:
        "This isn't your fault — we'll retry for free with no quota charge. Tap the button below to render again.",
      errorCodeLabel: "Error code: {{code}}",
      fallbackSuggestion: "Tip: try with the {{provider}} provider (slightly slower but more stable).",
      noQuotaCharge: "No quota was used for this failed render.",
      cta: "Retry for free",
    },
    quotaWarn: {
      subjectWarn: "You're running low on render quota",
      subjectFull: "You've used all your render quota",
      preheader: "You've used {{used}}/{{cap}} clips",
      headingWarn: "Almost out of monthly quota",
      headingFull: "Monthly quota is used up",
      body1:
        "You've used {{used}}/{{cap}} clips. Quota resets in {{days}} days, or upgrade to Pro to continue right away.",
      cta: "Upgrade to Pro",
      tip: "Pro: 150 videos/mo + no watermark + AI hero scene.",
    },
    paymentFailed: {
      subject: "Payment couldn't be processed",
      preheader: "Update your card to keep Pro benefits",
      heading: "Payment failed",
      body1:
        "Stripe couldn't charge the card on file. Your account stays on Pro through the end of this cycle — just update the card before the next retry to avoid being downgraded to Free.",
      attemptCount: "Attempt {{n}}",
      nextRetryAt: "Stripe will retry on {{date}}",
      cta: "Update payment method",
      supportLink: "Run into trouble? Email support@isale.app",
    },
  },
} as const;

export default en;
